import React, { useState, useEffect, useRef } from 'react';
import { TextField, IconButton, Paper, List, ListItem, ListItemIcon, ListItemText, Typography, Divider, Collapse } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ListIcon from '@mui/icons-material/List';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useService } from "contexts/ServiceContextProvider";
import TinyCustomFallback from 'components/Custom/TinyCustomFallback';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'contexts/SnackbarContextProvider';
import "../../css/components/Topbar/SearchBar.css";

const colors = {
  backgroundDark: '#2C2C2E',
  backgroundLight: '#3A3A3C',
  textPrimary: '#E0E0E0',
  textSecondary: '#A1A1A1',
  divider: '#48484A',
  inputBackground: '#1C1C1E',
  iconColor: '#F0F0F0',
  linkColor: '#ADD8E6',
};

const SearchComponent = ({ workspace_id, project_id }) => {
  // Initialize results based on project_id:
  const [results, setResults] = useState(project_id ? { boards: [] } : { projects: [] });
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const { SearchQueryingWorkspaceDatabase } = useService();
  const searchRef = useRef(null);
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbar();

  const [openItems, setOpenItems] = useState({});

  // When inside a project, auto-expand boards and lists.
  useEffect(() => {
    if (project_id && results.boards && results.boards.length > 0) {
      const newOpenItems = {};
      // Optionally, you can also set a key for the project itself if needed.
      newOpenItems[project_id] = true;
      results.boards.forEach(board => {
        newOpenItems[`board-${board.board_id}`] = true;
      });
      setOpenItems(newOpenItems);
    }
  }, [project_id, results.boards]);

  const toggleCollapse = (id) => {
    setOpenItems((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  const handleSearch = async () => {
    // Validation changed from minimum 3 character validation to 3 minimum BUT allow a minimum of 2 when hashtagging (for #1-9 tasks)
    if ((query.startsWith("#") && query.trim().length < 2) || (!query.startsWith("#") && query.trim().length < 3)) {
      showSnackbar(t("search.minimum_error"), 'error');
      return;
    }
        
    setLoading(true);
    try {
      const response = await SearchQueryingWorkspaceDatabase(workspace_id, project_id, query);
      setResults(response);
      setShowResults(true);
    } catch (error) {
      console.error('Error fetching search results:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
  };

  const handleClickOutside = (event) => {
    if (searchRef.current && !searchRef.current.contains(event.target)) {
      setShowResults(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleHashingOnClick = (task) => {
    if (project_id) {
      const hash = `#${task.hyperlink.split('#')[1]}-${Date.now()}`;
      window.location.hash = hash;
      setShowResults(false);
    } else {
      const url = task.hyperlink.startsWith('http') ? task.hyperlink : `/${task.hyperlink}`;
      window.location.href = url;
    }
  };
  

  const renderChecklists = (checklists) => (
    <List>
      {checklists.map((checklist, checklistIndex) => (
        <React.Fragment key={`checklist-${checklistIndex}`}>
          <ListItem sx={{ backgroundColor: colors.backgroundLight }}>
            <ListItemIcon>
              <CheckBoxIcon color={checklist.found_in_checklist ? 'primary' : 'action'} />
            </ListItemIcon>
            <ListItemText primary={checklist.checklist_title} sx={{ color: colors.textPrimary }} />
          </ListItem>
          {checklist.checklist_items?.map((item, itemIndex) => (
            <ListItem key={`item-${itemIndex}`} className="search-bar-item-padding" sx={{ backgroundColor: colors.backgroundLight }}>
              <ListItemIcon>
                <CheckBoxIcon color={item.found_in_item ? 'primary' : 'action'} />
              </ListItemIcon>
              <ListItemText
                primary={item.checklist_item_title}
                secondary={item.checked ? t('Completed') : t('Pending')}
                sx={{ color: colors.textPrimary }}
              />
            </ListItem>
          ))}
        </React.Fragment>
      ))}
    </List>
  );

  const renderTasks = (tasks) => (
    <List style={{ paddingTop: "16px" }}>
      {tasks.map((task, taskIndex) => (
        <div key={`task-${taskIndex}`}>
          <ListItem
            className="search-bar-task-padding"
            sx={{ backgroundColor: colors.backgroundLight }}
          >
            <ListItemIcon>
              <AssignmentIcon
                color={task.found_in.includes('description') ? 'primary' : 'inherit'}
                style={{ color: task.found_in.includes('description') ? undefined : '#FFFFFF' }}
              />
            </ListItemIcon>
            <ListItemText
              primary={task.task_title}
              secondary={task.found_in && task.found_in.length > 0 ? `${t('search.found')} ${task.found_in.join(', ')}` : null}
              sx={{
                color: colors.textPrimary,
                '& .MuiListItemText-secondary': { color: '#B0B0B0' },
              }}
              className="search-bar-task-text"
            />
            <IconButton onClick={(e) => handleHashingOnClick(task)} className="search-bar-icon-margin">
              <OpenInNewIcon style={{ color: colors.linkColor }} />
            </IconButton>
          </ListItem>
          {renderChecklists(task.checklists)}
          <Divider sx={{ backgroundColor: colors.divider }} />
        </div>
      ))}
    </List>
  );

  const renderLists = (lists, boardId) => (
    <Collapse in={openItems[boardId]} timeout="auto" unmountOnExit>
      <List>
        {lists.map((list, listIndex) => (
          <React.Fragment key={`list-${listIndex}`}>
            <ListItem
              button
              onClick={() => toggleCollapse(`list-${list.list_id}`)}
              className="search-bar-list-padding"
              sx={{ backgroundColor: colors.backgroundLight }}
            >
              <ListItemIcon>
                <ListIcon style={{ color: colors.iconColor }} />
              </ListItemIcon>
              <ListItemText primary={`${t('search.prefix_list')} ${list.list_title}`} sx={{ color: colors.textPrimary }} />
              {openItems[`list-${list.list_id}`] ? (
                <ExpandLessIcon style={{ color: colors.iconColor }} />
              ) : (
                <ExpandMoreIcon style={{ color: colors.iconColor }} />
              )}
            </ListItem>
            <Collapse in={openItems[`list-${list.list_id}`]} timeout="auto" unmountOnExit>
              {renderTasks(list.tasks)}
            </Collapse>
            <Divider sx={{ backgroundColor: colors.divider }} />
          </React.Fragment>
        ))}
      </List>
    </Collapse>
  );

  // For project searches, we expect results.boards to be returned.
  const renderBoards = (boards, projectId) => (
    <Collapse in={openItems[projectId]} timeout="auto" unmountOnExit>
      <List>
        {boards.map((board, boardIndex) => (
          <React.Fragment key={`board-${boardIndex}`}>
            <ListItem
              button
              onClick={() => toggleCollapse(`board-${board.board_id}`)}
              className="search-bar-boards-padding"
              sx={{ backgroundColor: colors.backgroundLight }}
            >
              <ListItemIcon>
                <DashboardIcon style={{ color: colors.iconColor }} />
              </ListItemIcon>
              <ListItemText primary={`${t('search.prefix_board')} ${board.board_name}`} sx={{ color: colors.textPrimary }} />
              {openItems[`board-${board.board_id}`] ? (
                <ExpandLessIcon style={{ color: colors.iconColor }} />
              ) : (
                <ExpandMoreIcon style={{ color: colors.iconColor }} />
              )}
            </ListItem>
            {renderLists(board.lists, `board-${board.board_id}`)}
            <Divider sx={{ backgroundColor: colors.divider }} />
          </React.Fragment>
        ))}
      </List>
    </Collapse>
  );

  // For workspace searches, render projects as before.
  const renderProjects = () => (
    <List>
      {results.projects.map((project, projectIndex) => {
        const hasBoards = project.boards && project.boards.length > 0;
        return (
          <React.Fragment key={`project-${projectIndex}`}>
            <ListItem
              button={hasBoards}
              onClick={() => hasBoards && toggleCollapse(`project-${project.project_id}`)}
              sx={{ backgroundColor: colors.backgroundLight }}
            >
              <ListItemIcon>
                <AccountTreeIcon color="primary" style={{ color: colors.iconColor }} />
              </ListItemIcon>
              <ListItemText primary={`${t('search.prefix_project')} ${project.project_name}`} sx={{ color: colors.textPrimary }} />
              <IconButton
                href={`/${project.project_hyperlink}`}
                onClick={(e) => e.stopPropagation()}
                className="search-bar-icon-margin"
              >
                <OpenInNewIcon style={{ color: colors.linkColor }} />
              </IconButton>
              {hasBoards &&
                (openItems[`project-${project.project_id}`] ? (
                  <ExpandLessIcon style={{ color: colors.iconColor }} />
                ) : (
                  <ExpandMoreIcon style={{ color: colors.iconColor }} />
                ))}
            </ListItem>
            {hasBoards && renderBoards(project.boards, `project-${project.project_id}`)}
            <Divider sx={{ backgroundColor: colors.divider }} />
          </React.Fragment>
        );
      })}
    </List>
  );

  return (
    <div className="search-bar-bar" ref={searchRef}>
      <TextField
        placeholder={t('search.placeholder')}
        value={query}
        onChange={handleInputChange}
        onKeyPress={handleKeyPress}
        InputProps={{
          endAdornment: loading ? (
            <TinyCustomFallback />
          ) : (
            <IconButton onClick={handleSearch} disabled={loading}>
              <SearchIcon style={{ color: colors.iconColor }} />
            </IconButton>
          ),
          style: { backgroundColor: colors.inputBackground, color: colors.textPrimary },
        }}
        fullWidth
        autoComplete="off"
      />


      

      {showResults && query && (
        <Paper className="search-bar-results" style={{ backgroundColor: colors.backgroundDark }}>
          {project_id ? (
            results.boards && results.boards.length > 0 ? (
              renderBoards(results.boards, project_id)
            ) : (
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                {t('search.no_results')}
              </Typography>
            )
          ) : (
            results.projects && results.projects.length > 0 ? (
              renderProjects()
            ) : (
              <Typography variant="body2" sx={{ color: colors.textSecondary }}>
                {t('search.no_results')}
              </Typography>
            )
          )}
        </Paper>
      )}
    </div>
  );
};

export default SearchComponent;
